import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Flex, useDisclosure } from "@chakra-ui/react";
import ZoomImage from "components/DispatchCorrections/ZoomedImage";
import { Modalcustomizable } from "components/modal/ModalPass";
import { LabelGroup } from "pages/RefProductInter/LabelGroup";
import { MdOutlineImage } from "react-icons/md";

/**
 * Helper para obtener la extensión visualmente
 */
const getExtension = (filename) => {
  if (!filename) return "";
  const lastDotIndex = filename.lastIndexOf(".");
  if (lastDotIndex === -1 || lastDotIndex === 0) return "";
  const extension = filename.slice(lastDotIndex + 1).toLowerCase();
  const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "tiff"];
  return imageExtensions.includes(extension)
    ? "Imagen"
    : extension.charAt(0).toUpperCase() + extension.slice(1);
};

export const GroupInputUpload = ({
  onClose = () => {},
  customHandleChange = null, // Objeto con { values, handleInputChange, reset }
  mode = "normal",
  preview = false,
  callBack = () => {}, // Función que recibe la metadata confirmada
  isLoading = false // Estado de carga (subida a S3)
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fechasHoy, setFechasHoy] = useState(null);

  // Extraemos los valores y funciones del prop customHandleChange (pasado por DocumentUploader)
  const values = customHandleChange?.values || {};
  const handleInputChange = customHandleChange?.handleInputChange || (() => {});

  const {
    isOpen: isOpenPreview,
    onOpen: onOpenPreview,
    onClose: onClosePreview,
  } = useDisclosure();

  useEffect(() => {
    setFechasHoy(new Date().toISOString().substring(0, 10));
  }, []);

  // Determinar extensión para previsualización
  const extension = values?.extension || getExtension(values?.name || "");

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  const openImageView = useCallback(() => {
    setIsFullscreen(false);
    onOpenPreview();
  }, [onOpenPreview]);

  const previewValidation = (extension === "Imagen" || extension === "Pdf") && preview;

  /**
   * Acción principal: Confirmar Metadata
   * Envia los datos al DocumentUploader para que inicie la subida a S3.
   */
  const handleConfirm = useCallback(() => {
    if (callBack) {
      callBack(values);
    } else {
      onClose();
    }
  }, [values, callBack, onClose]);

  return (
    <>
      {/* Modal de Previsualización interna */}
      <Modalcustomizable
        title={"Previsualización"}
        isOpen={isOpenPreview}
        onClose={onClosePreview}
        rightButtonText={"Confirmar"}
        iconComponent={MdOutlineImage}
        hiddenButtons={true}
        size={isFullscreen ? "full" : "4xl"}
      >
        {extension === "Imagen" && (
          <ZoomImage
            src={values?.url}
            toggleFullscreen={toggleFullscreen}
            isFullscreen={isFullscreen}
            setIsFullscreen={setIsFullscreen}
          />
        )}
        {extension === "Pdf" && (
          <iframe
            title="preview"
            src={values?.url || ""}
            width="100%"
            height="500px"
          />
        )}
      </Modalcustomizable>

      {/* Formulario de Metadata */}
      <Box
        display={"flex"}
        flexDir={"column"}
        justifyContent={"center"}
        marginBottom={"2rem"}
      >
        {/* Solo mostrar nombre editable si estamos en modo edición explícito */}
        {mode === "edit" && (
          <LabelGroup
            name={"name"}
            value={values?.name || ""}
            onChange={handleInputChange}
            tag={"Nombre Documento"}
            size={100}
            desicion={true}
            maxiLength={"50"}
          />
        )}

        {values?.attachment_description && (
          <LabelGroup
            name={"attachment_description"}
            onChange={handleInputChange}
            value={values?.attachment_description || ""}
            tag={"Descripción"}
            size={100}
            desicion={values?.nameDocument}
            maxiLength={"50"}
          />
        )}
        <LabelGroup
          name={"attachment_number"}
          onChange={handleInputChange}
          value={values?.attachment_number || ""}
          tag={"Número del anexo"}
          size={100}
          desicion={!!previewValidation}
        />

        <LabelGroup
          name={"attachment_date"}
          onChange={handleInputChange}
          value={values?.attachment_date || ""}
          type={"date"}
          tag={"Fecha del anexo"}
          size={100}
          desicion={false}
          max={fechasHoy}
        />

        <LabelGroup
          name="attachment_comment"
          value={values?.attachment_comment || ""}
          onChange={handleInputChange}
          tag={"Comentario"}
          size={100}
          desicion={!!previewValidation}
          maxiLength={"50"}
        />
      </Box>

      {/* Botones de Acción */}
      <Flex className="my-3" justifyContent={"space-between"}>
        <Button
          colorScheme="gray"
          onClick={onClose}
          isDisabled={isLoading}
        >
          Cancelar
        </Button>

        <Button
          colorScheme="blue"
          onClick={previewValidation ? openImageView : handleConfirm}
          isLoading={isLoading} // Spinner de carga nativo de Chakra
          loadingText="Subiendo..."
          // Deshabilitar si es solo preview o si está cargando
          isDisabled={isLoading}
        >
          {previewValidation ? "Ver Documento" : "Guardar y Subir"}
        </Button>
      </Flex>
    </>
  );
};